import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { bsToAD, bsDaysInMonth, fmtAD, BS_MONTHS_FULL } from './bs';
import type { ShiftLogEntry } from '../services/api';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Equip { id: number; name: string; type: 'generator' | 'transformer' }

export type ShiftLogPowerUnit  = 'kW'  | 'MW';
export type ShiftLogEnergyUnit = 'kWh' | 'MWh' | 'GWh';

function pFactor(u: ShiftLogPowerUnit):  number { return u === 'MW'  ? 1e-3 : 1; }
function eFactor(u: ShiftLogEnergyUnit): number { return u === 'MWh' ? 1e-3 : u === 'GWh' ? 1e-6 : 1; }
function pLabel(u: ShiftLogPowerUnit):   string { return u === 'MW'  ? 'MW'  : 'KW';  }
function rLabel(u: ShiftLogPowerUnit):   string { return u === 'MW'  ? 'MVAR' : 'KVAR'; }
function eLabel(u: ShiftLogEnergyUnit):  string { return u === 'MWh' ? 'MWH' : u === 'GWh' ? 'GWH' : 'KWH'; }

function applyConv(v: number | null, factor: number): number | null {
  return v == null ? null : +(v * factor).toFixed(6);
}

// ─── Column mapping within each 12-col panel ──────────────────────────────────
// col+0: Time (hour), col+1: KW, col+2: KVAR, col+3: CosØ (PF)
// col+4: R-Y, col+5: Y-B, col+6: B-R, col+7: Hz
// col+8: I1, col+9: I2, col+10: I3, col+11: KWH

function extractFields(entry: ShiftLogEntry) {
  return [
    entry.output_kw     ?? null,
    entry.reactive_kvar ?? null,
    entry.power_factor  ?? null,
    entry.voltage_ry_kv ?? null,
    entry.voltage_yb_kv ?? null,
    entry.voltage_br_kv ?? null,
    entry.frequency_hz  ?? null,
    entry.current_i1_a  ?? null,
    entry.current_i2_a  ?? null,
    entry.current_i3_a  ?? null,
    entry.energy_kwh    ?? null,
  ];
}

// ─── Styling ───────────────────────────────────────────────────────────────────

const PANEL_WIDTH = 12; // columns per equipment panel: Time + 11 data fields

// Sub-header groups within a panel, as [startOffset, endOffset] (0-indexed, inclusive)
// relative to the panel's first column — must stay in sync with `labels` below.
const FIELD_GROUPS: { label: string; from: number; to: number }[] = [
  { label: 'Generator Output',        from: 1,  to: 2  }, // KW, KVAR
  { label: 'PF',                      from: 3,  to: 3  },
  { label: 'Generating Voltage (kv)', from: 4,  to: 6  }, // R-Y, Y-B, B-R
  { label: 'Freq.',                   from: 7,  to: 7  },
  { label: 'Generating Current (A)',  from: 8,  to: 10 }, // I1, I2, I3
  { label: 'Energy',                  from: 11, to: 11 },
];

const THIN_BORDER:  ExcelJS.Border = { style: 'thin',   color: { argb: 'FFB0B8C4' } };
const PANEL_BORDER: ExcelJS.Border = { style: 'medium', color: { argb: 'FF44546A' } };

const TITLE_FILL:  ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2F5496' } };
const HEADER_FILL: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
const GROUP_FILL:  ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDF1F9' } };
const BAND_FILL:   ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF7F9FC' } };

/** Grid border on all four sides of a cell (thin by default). */
function gridBorder(style: ExcelJS.Border = THIN_BORDER): Partial<ExcelJS.Borders> {
  return { top: style, left: style, bottom: style, right: style };
}

/**
 * Style a single cell or a merged rectangular range (r1..r2, c1..c2).
 * The value is only ever written to the top-left (anchor) cell — ExcelJS
 * forbids assigning values to the other cells of a merged range — but the
 * font/fill/border are applied to every cell in the range so the merged
 * box renders as one consistent, fully-bordered unit.
 */
function styleRange(
  ws: ExcelJS.Worksheet,
  r1: number, c1: number, r2: number, c2: number,
  value: string | number | null,
  opts: {
    bold?: boolean; italic?: boolean; size?: number; color?: string;
    align?: ExcelJS.Alignment['horizontal']; valign?: ExcelJS.Alignment['vertical'];
    fill?: ExcelJS.Fill; border?: Partial<ExcelJS.Borders>; numFmt?: string;
  } = {}
): void {
  if (r1 !== r2 || c1 !== c2) ws.mergeCells(r1, c1, r2, c2);
  const font = { bold: !!opts.bold, italic: !!opts.italic, size: opts.size ?? 10, color: { argb: opts.color ?? 'FF1F2937' } };
  const alignment: Partial<ExcelJS.Alignment> = { horizontal: opts.align ?? 'center', vertical: opts.valign ?? 'middle', wrapText: true };
  const border = opts.border ?? gridBorder();
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      const cell = ws.getCell(r, c);
      if (r === r1 && c === c1) cell.value = value;
      cell.font = font;
      cell.alignment = alignment;
      if (opts.fill) cell.fill = opts.fill;
      cell.border = border;
      if (opts.numFmt) cell.numFmt = opts.numFmt;
    }
  }
}

// ─── Export ────────────────────────────────────────────────────────────────────

export async function exportMonthToExcel(
  bsYear:     number,
  bsMonth:    number, // 0-indexed
  logsByDate: Record<string, ShiftLogEntry[]>,
  generators: { id: number; name: string }[],
  transformers: { id: number; name: string }[],
  companyName  = 'Mailun Khola Jal Vidhyut Company Limited',
  powerUnit:  ShiftLogPowerUnit  = 'kW',
  energyUnit: ShiftLogEnergyUnit = 'kWh',
): Promise<void> {
  const wb  = new ExcelJS.Workbook();
  const pf  = pFactor(powerUnit);
  const ef  = eFactor(energyUnit);

  const panels: Equip[] = [
    ...generators.map(g => ({ ...g, type: 'generator' as const })),
    ...transformers.map(t => ({ ...t, type: 'transformer' as const })),
  ];

  const daysInMonth = bsDaysInMonth(bsYear, bsMonth);
  const labels = [pLabel(powerUnit), rLabel(powerUnit), 'CosØ', 'R-Y', 'Y-B', 'B-R', 'Hz', 'I1', 'I2', 'I3', eLabel(energyUnit)];
  const numCols = panels.length * PANEL_WIDTH;

  // Row layout (1-indexed, must stay in sync with parseExcelForImport/autoDetectBSYearMonth):
  //   1: company name   2: BS date   3: Time + machine name   4: field groups   5: column labels
  //   6-29: hourly data (hour 0-23)   30-31: blank   32: signature lines   33: role labels
  const HOUR_ROW_START = 6;
  const SIG_ROW = 32;
  const ROLE_ROW = 33;
  const LAST_ROW = 33;

  for (let day = 1; day <= daysInMonth; day++) {
    const adDate = bsToAD(bsYear, bsMonth, day);
    const dateStr = fmtAD(adDate);
    const bsDateLabel = `Date:${bsYear}/${String(bsMonth + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
    const logs = logsByDate[dateStr] ?? [];

    const ws = wb.addWorksheet(String(day).padStart(2, '0'));
    ws.views = [{ state: 'frozen', ySplit: 5 }]; // keep headers visible while scrolling hours

    for (let c = 1; c <= numCols; c++) {
      ws.getColumn(c).width = (c - 1) % PANEL_WIDTH === 0 ? 7 : 10;
    }
    ws.getRow(1).height = 20;
    ws.getRow(3).height = 18;

    panels.forEach((eq, p) => {
      const base = p * PANEL_WIDTH;

      // Row 1 — company name banner
      styleRange(ws, 1, base + 1, 1, base + PANEL_WIDTH, companyName, {
        bold: true, size: 11, color: 'FFFFFFFF', fill: TITLE_FILL, border: gridBorder(PANEL_BORDER),
      });

      // Row 2 — BS date label. The label itself must stay anchored at column offset 9
      // (i.e. base + 10) — autoDetectBSYearMonth reads row[1][9] to find it.
      styleRange(ws, 2, base + 1, 2, base + 9, null, { fill: HEADER_FILL, border: gridBorder(PANEL_BORDER) });
      styleRange(ws, 2, base + 10, 2, base + PANEL_WIDTH, bsDateLabel, {
        italic: true, align: 'right', fill: HEADER_FILL, border: gridBorder(PANEL_BORDER),
      });

      // Row 3 — "Time" (spans rows 3-5) + machine name title bar
      styleRange(ws, 3, base + 1, 5, base + 1, 'Time', {
        bold: true, fill: HEADER_FILL, border: gridBorder(PANEL_BORDER),
      });
      styleRange(ws, 3, base + 2, 3, base + PANEL_WIDTH, eq.name, {
        bold: true, size: 11, color: 'FFFFFFFF', fill: TITLE_FILL, border: gridBorder(PANEL_BORDER),
      });

      // Row 4 — field group sub-headers
      FIELD_GROUPS.forEach(g => {
        styleRange(ws, 4, base + 1 + g.from, 4, base + 1 + g.to, g.label, { bold: true, fill: GROUP_FILL });
      });

      // Row 5 — column labels (KW, KVAR, …), thicker bottom border to close off the header block
      labels.forEach((lbl, i) => {
        styleRange(ws, 5, base + 2 + i, 5, base + 2 + i, lbl, {
          bold: true, fill: HEADER_FILL, border: { ...gridBorder(), bottom: PANEL_BORDER },
        });
      });

      // Rows 6-29 — hours 1-24 (DB hours 0-23), banded every other row for readability
      for (let hour = 0; hour < 24; hour++) {
        const row = HOUR_ROW_START + hour;
        const fill = hour % 2 === 1 ? BAND_FILL : undefined;

        styleRange(ws, row, base + 1, row, base + 1, hour + 1, { bold: true, fill });

        const entry = logs.find(l =>
          l.equipment_type === eq.type && l.equipment_id === eq.id && l.hour === hour
        );
        const fields = entry ? extractFields(entry) : Array<number | null>(11).fill(null);
        if (entry) {
          fields[0]  = applyConv(fields[0],  pf); // output_kw
          fields[1]  = applyConv(fields[1],  pf); // reactive_kvar
          fields[10] = applyConv(fields[10], ef); // energy_kwh
        }
        fields.forEach((v, i) => {
          styleRange(ws, row, base + 2 + i, row, base + 2 + i, v, { fill, numFmt: '#,##0.00' });
        });
      }

      // Footer — row 32: signature lines (blank cells with underline), row 33: role labels
      styleRange(ws, SIG_ROW, base + 2,  SIG_ROW, base + 4,  '', { border: { bottom: THIN_BORDER } });
      styleRange(ws, SIG_ROW, base + 6,  SIG_ROW, base + 8,  '', { border: { bottom: THIN_BORDER } });
      styleRange(ws, SIG_ROW, base + 10, SIG_ROW, base + 12, '', { border: { bottom: THIN_BORDER } });

      styleRange(ws, ROLE_ROW, base + 3,  ROLE_ROW, base + 3,  'Day Shift',     { italic: true, size: 9, border: {} });
      styleRange(ws, ROLE_ROW, base + 7,  ROLE_ROW, base + 7,  'Night Shift',   { italic: true, size: 9, border: {} });
      styleRange(ws, ROLE_ROW, base + 11, ROLE_ROW, base + 11, 'Plant Manager', { italic: true, size: 9, border: {} });

      // Thick outer border down both sides of the panel to separate it from its neighbours
      for (let r = 1; r <= LAST_ROW; r++) {
        ws.getCell(r, base + 1).border              = { ...ws.getCell(r, base + 1).border, left: PANEL_BORDER };
        ws.getCell(r, base + PANEL_WIDTH).border     = { ...ws.getCell(r, base + PANEL_WIDTH).border, right: PANEL_BORDER };
      }
    });
  }

  // Use blob download (more reliable in browser than triggering a file-system write)
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${BS_MONTHS_FULL[bsMonth].toUpperCase()}_${bsYear}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Auto-detect BS year/month from Excel header ─────────────────────────────

export function autoDetectBSYearMonth(file: File): Promise<{ year: number; month: number } | null> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onerror = () => resolve(null);
    reader.onload = e => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target!.result as ArrayBuffer), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        if (!ws) { resolve(null); return; }
        const rows: (string | number | null)[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
        // Row 1, col 9 has "Date:YYYY/M/D"
        const cell = rows[1]?.[9];
        if (typeof cell !== 'string') { resolve(null); return; }
        const m = cell.match(/Date:(\d+)\/(\d+)\/(\d+)/i);
        if (!m) { resolve(null); return; }
        resolve({ year: parseInt(m[1], 10), month: parseInt(m[2], 10) - 1 });
      } catch { resolve(null); }
    };
    reader.readAsArrayBuffer(file);
  });
}

// ─── Import ────────────────────────────────────────────────────────────────────

export function parseExcelForImport(
  file: File,
  generators: { id: number; name: string }[],
  transformers: { id: number; name: string }[],
  bsYear:     number,
  bsMonth:    number,
  powerUnit:  ShiftLogPowerUnit  = 'kW',
  energyUnit: ShiftLogEnergyUnit = 'kWh',
): Promise<{ date: string; entries: ShiftLogEntry[] }[]> {
  // Inverse factors: convert from file unit back to kW/kWh for DB storage
  const pInv = 1 / pFactor(powerUnit);
  const eInv = 1 / eFactor(energyUnit);
  const panels: Equip[] = [
    ...generators.map(g => ({ ...g, type: 'generator' as const })),
    ...transformers.map(t => ({ ...t, type: 'transformer' as const })),
  ];

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = e => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target!.result as ArrayBuffer), { type: 'array' });
        const results: { date: string; entries: ShiftLogEntry[] }[] = [];

        for (const sheetName of wb.SheetNames) {
          const day = parseInt(sheetName, 10);
          if (isNaN(day) || day < 1 || day > 32) continue;

          let adDate: Date;
          try { adDate = bsToAD(bsYear, bsMonth, day); } catch { continue; }
          const dateStr = fmtAD(adDate);

          const rows: (string | number | null)[][] = XLSX.utils.sheet_to_json(
            wb.Sheets[sheetName], { header: 1, defval: null }
          );

          const entries: ShiftLogEntry[] = [];

          // Data starts at row index 5 (after 5 header rows), 24 data rows
          for (const row of rows.slice(5)) {
            if (!row) continue;

            // Check the first panel's time column is a valid hour
            const firstHour = Number(row[0]);
            if (isNaN(firstHour) || firstHour < 1 || firstHour > 24) continue;

            for (let p = 0; p < panels.length; p++) {
              const eq = panels[p];
              const base = p * 12;
              const excelHour = Number(row[base]);
              if (isNaN(excelHour) || excelHour < 1 || excelHour > 24) continue;

              const n = (offset: number): number | null => {
                const v = row[base + offset];
                if (v == null || v === '') return null;
                const num = Number(v);
                return isNaN(num) ? null : num;
              };

              // Skip panels where every data field is null (e.g. offline machine)
              const hasData = [1,2,3,4,5,6,7,8,9,10,11].some(i => n(i) !== null);
              if (!hasData) continue;

              const dbHour = excelHour - 1; // 1-based Excel → 0-based DB

              const conv = (v: number | null, factor: number) =>
                v == null ? null : +(v * factor).toFixed(6);

              entries.push({
                hour:            dbHour,
                equipment_type:  eq.type,
                equipment_id:    eq.id,
                output_kw:       conv(n(1),  pInv),
                reactive_kvar:   conv(n(2),  pInv),
                power_factor:    n(3),
                voltage_ry_kv:   n(4),
                voltage_yb_kv:   n(5),
                voltage_br_kv:   n(6),
                frequency_hz:    n(7),
                current_i1_a:    n(8),
                current_i2_a:    n(9),
                current_i3_a:    n(10),
                energy_kwh:      conv(n(11), eInv),
              });
            }
          }

          if (entries.length > 0) results.push({ date: dateStr, entries });
        }

        resolve(results);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}
